/* Self-contained custom element: each section owns its state and listeners. */
if (!customElements.get('mv-customer-types')) {
  customElements.define('mv-customer-types', class extends HTMLElement {
    static scrollLocks = 0;
    static scrollState = null;
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      const signal = this._controller.signal;
      this._nav = this.querySelector('[data-mvct-nav]');
      this._tabs = [...this.querySelectorAll('[data-mvct-tab]')];
      this._panels = [...this.querySelectorAll('[data-mvct-panel]')];
      this._line = this.querySelector('.mvct-underline');
      if (!this._tabs.length || !this._nav || !this._line) return;
      this._active = Math.max(0, this._tabs.findIndex(t => t.dataset.profile === this.dataset.default));
      this._nav.setAttribute('role', 'tablist');
      this._nav.setAttribute('aria-label', 'Find your morning');
      this._tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this._panels[index].id);
        tab.addEventListener('click', event => { event.preventDefault(); this.select(index, false, true); }, {signal});
        tab.addEventListener('keydown', event => {
          let next = index;
          const rtl = getComputedStyle(this).direction === 'rtl';
          if (event.key === 'ArrowRight') next = (index + (rtl ? -1 : 1) + this._tabs.length) % this._tabs.length;
          else if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : -1) + this._tabs.length) % this._tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this._tabs.length - 1;
          else return;
          event.preventDefault(); this.select(next, true, true);
        }, {signal});
      });
      this._panels.forEach(panel => { panel.setAttribute('role', 'tabpanel'); panel.setAttribute('tabindex', '0'); });
      this.setAttribute('data-enhanced', '');
      this.select(this._active, false, false);
      this._nav.addEventListener('scroll', () => this.positionLine(), {signal, passive:true});
      this._resize = new ResizeObserver(() => this.select(this._active, false, false));
      this._resize.observe(this._nav);
      if (document.fonts) document.fonts.ready.then(() => { if(this.isConnected) this.positionLine(); });
      document.addEventListener('shopify:block:select', event => {
        const index = this._panels.findIndex(p => p.dataset.blockId === event.detail?.blockId);
        if(index >= 0) this.select(index, false, true);
      }, {signal});
      this.setupReviews(signal);
    }
    select(index, focus, animate) {
      this._active = index;
      this._tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.setAttribute('tabindex', i === index ? '0' : '-1'); });
      this._panels.forEach((panel, i) => {
        const selected = i === index;
        panel.setAttribute('aria-hidden', String(!selected));
        panel.inert = !selected;
        if (selected) {
          panel.querySelectorAll('[data-mvct-source]').forEach(source => source.media = '(min-width: 0px)');
          if (animate) panel.querySelectorAll('img').forEach(image => image.loading = 'eager');
        }
        panel.classList.toggle('is-active', selected);
        panel.classList.toggle('is-entering', selected && animate);
      });
      const tab = this._tabs[index];
      if (focus) tab.focus({preventScroll:true});
      const navRect = this._nav.getBoundingClientRect(), tabRect = tab.getBoundingClientRect();
      if (tabRect.left < navRect.left || tabRect.right > navRect.right) {
        const offset = tabRect.left < navRect.left ? tabRect.left - navRect.left - 12 : tabRect.right - navRect.right + 20;
        this._nav.scrollBy({left:offset, behavior:animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant'});
      }
      this.positionLine();
    }
    positionLine() {
      const tab = this._tabs?.[this._active];
      if(!tab || !this._nav) return;
      const rect = tab.getBoundingClientRect(), navRect = this._nav.getBoundingClientRect();
      this._line.style.width = `${rect.width - parseFloat(getComputedStyle(tab).paddingRight)}px`;
      this._line.style.transform = `translateX(${rect.left - navRect.left + this._nav.scrollLeft}px)`;
      this._nav.parentElement.classList.toggle('at-end', this._nav.scrollWidth - this._nav.clientWidth - this._nav.scrollLeft < 3);
    }
    setupReviews(signal) {
      this._dialog = this.querySelector('[data-mvct-dialog]');
      if (!this._dialog || typeof this._dialog.showModal !== 'function') return;
      this.setAttribute('data-review-ready', '');
      this.querySelectorAll('[data-mvct-review-trigger]').forEach(trigger => {
        trigger.disabled = false;
        trigger.addEventListener('click', () => this.openReview(trigger), {signal});
      });
      this._dialog.querySelector('[data-mvct-close]').addEventListener('click', () => this.closeReview(), {signal});
      this._dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeReview(); }, {signal});
      this._dialog.addEventListener('click', event => {
        if(event.target !== this._dialog) return;
        const rect = this._dialog.querySelector('.mvct-review-sheet').getBoundingClientRect();
        if(event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeReview();
      }, {signal});
      this._dialog.addEventListener('keydown', event => {
        if(event.key !== 'Tab') return;
        const focusable = [...this._dialog.querySelectorAll('button:not([disabled]), a[href]:not([hidden])')];
        const first = focusable[0], last = focusable[focusable.length - 1];
        if(event.shiftKey && document.activeElement === first){event.preventDefault();last?.focus();}
        else if(!event.shiftKey && document.activeElement === last){event.preventDefault();first?.focus();}
      }, {signal});
      this._dialog.addEventListener('close', () => this.restoreReview(), {signal});
    }
    openReview(trigger) {
      if(this._dialog.open) return;
      const panel = trigger.closest('[data-mvct-panel]');
      if(!panel?.classList.contains('is-active')) return;
      const original = panel.querySelector('[data-mvct-review-original]');
      if(!original?.textContent.trim()) return;
      const rating = Math.min(5, Math.max(1, Number(panel.querySelector('[data-review-rating]')?.dataset.reviewRating) || 5));
      this._dialog.querySelector('[data-mvct-modal-name]').textContent = panel.querySelector('[data-mvct-review-name]').textContent;
      this._dialog.querySelector('[data-mvct-modal-text]').textContent = original.textContent;
      const stars = this._dialog.querySelector('[data-mvct-modal-stars]');
      stars.setAttribute('aria-label', `${rating} out of 5 stars`);
      stars.replaceChildren();
      const glyphs = document.createElement('span');
      glyphs.setAttribute('aria-hidden','true');
      glyphs.textContent = '★'.repeat(rating) + '☆'.repeat(5-rating);
      stars.append(glyphs);
      const source = panel.querySelector('[data-mvct-review-source]');
      const link = this._dialog.querySelector('[data-mvct-modal-source]');
      link.hidden = !source;
      if(source) link.setAttribute('href', source.getAttribute('href'));
      else link.removeAttribute('href');
      this._reviewTrigger = trigger;
      this._dialog.showModal();
      this.lockPage();
      this._dialog.querySelector('.mvct-review-sheet').scrollTop = 0;
      this._dialog.querySelector('[data-mvct-close]').focus({preventScroll:true});
      this._openFrame = requestAnimationFrame(() => { if(this._dialog.open) this._dialog.classList.add('is-open'); });
    }
    closeReview() {
      if(!this._dialog?.open || this._closingReview) return;
      this._closingReview = true;
      cancelAnimationFrame(this._openFrame);
      this._dialog.classList.remove('is-open');
      this._reviewTimer = setTimeout(() => { this._dialog.close(); }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 250);
    }
    restoreReview() {
      clearTimeout(this._reviewTimer);
      cancelAnimationFrame(this._openFrame);
      this._dialog?.classList.remove('is-open');
      this._closingReview = false;
      this.unlockPage();
      if(this._reviewTrigger?.isConnected && !this._reviewTrigger.closest('[inert]')) this._reviewTrigger.focus({preventScroll:true});
      this._reviewTrigger = null;
    }
    lockPage() {
      if(this._pageLocked) return;
      this._pageLocked = true;
      const Class = this.constructor;
      if(Class.scrollLocks++ > 0) return;
      const body = document.body;
      const properties = ['position','top','left','right','width','overflow','paddingRight'];
      Class.scrollState = {x:window.scrollX, y:window.scrollY, values:Object.fromEntries(properties.map(key => [key, body.style[key]]))};
      const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      body.style.paddingRight = `${parseFloat(getComputedStyle(body).paddingRight) + gap}px`;
      body.style.position = 'fixed'; body.style.top = `-${Class.scrollState.y}px`;
      body.style.left = '0'; body.style.right = '0'; body.style.width = '100%'; body.style.overflow = 'hidden';
    }
    unlockPage() {
      if(!this._pageLocked) return;
      this._pageLocked = false;
      const Class = this.constructor;
      if(--Class.scrollLocks > 0 || !Class.scrollState) return;
      const {x,y,values} = Class.scrollState;
      Object.entries(values).forEach(([key,value]) => document.body.style[key] = value);
      window.scrollTo({left:x,top:y,behavior:'instant'});
      Class.scrollState = null;
    }
    disconnectedCallback() {
      if(this._dialog?.open) this._dialog.close();
      this.restoreReview();
      this.removeAttribute('data-review-ready');
      this._controller?.abort(); this._resize?.disconnect(); this._controller = null;
    }
  });
}
